import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../App.css';
import './DashboardPage.css';
import api from '../../utils/api.js';
import { handleApiError } from '../../utils/errorHandler.js';
import TruncatedCell from '../../components/TruncatedCell.js';
import Pagination from '../../components/Pagination.js';
import usePaginatedFetch from '../../utils/usePaginatedFetch.js';
import AiResultModal from '../../components/AiResultModal.js';
import UserSelect from '../../components/UserSelect.js';
import { useAuth } from '../../contexts/AuthContext.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';
import { jstDateDaysAgo } from '../../utils/dateFormatter.js';

function DashboardPage() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();


    const [loading, setLoading] = useState(true);

    // --- 検索 ---
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // --- 作成モーダル ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newApplicantName, setNewApplicantName] = useState('');
    const [newOwnerName, setNewOwnerName] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);

    // --- フィードバック振り返り ---
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryResult, setSummaryResult] = useState(null);
    const [summaryError, setSummaryError] = useState(null);

    const DAYS_RANGE = 14;

    const dateFrom = useMemo(() => jstDateDaysAgo(DAYS_RANGE), []);

    // --- 3セクションを usePaginatedFetch で管理 ---
    const activeParams = useMemo(() => ({
        owner_id: currentUser?.id,
        is_closed: 'false',
        applicant_name: searchQuery || undefined,
    }), [currentUser?.id, searchQuery]);

    const closedParams = useMemo(() => ({
        owner_id: currentUser?.id,
        is_closed: 'true',
        close_date_from: dateFrom,
    }), [currentUser?.id, dateFrom]);

    const meetingsParams = useMemo(() => ({
        owner_id: currentUser?.id,
        meeting_date_from: dateFrom,
    }), [currentUser?.id, dateFrom]);

    const activeOpps = usePaginatedFetch('/opportunities/search/', activeParams, 5);
    const closedOpps = usePaginatedFetch('/opportunities/search/', closedParams, 5);
    const meetings = usePaginatedFetch('/meetings/search/', meetingsParams, 5);

    useEffect(() => {
        setLoading(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // currentUser, 検索条件, ページが変わったらアクティブ案件を取得
    useEffect(() => {
        if (currentUser) activeOpps.fetch();
    }, [currentUser, activeParams, activeOpps.page]); // eslint-disable-line react-hooks/exhaustive-deps

    // 終了案件・活動は初期 + ページ変更時に取得（currentUser がロード済みであることを保証）
    useEffect(() => {
        if (currentUser) closedOpps.fetch();
    }, [currentUser, closedParams, closedOpps.page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (currentUser) meetings.fetch();
    }, [currentUser, meetingsParams, meetings.page]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- ハンドラ ---
    const handleSearchClick = () => {
        activeOpps.setPage(1);
        setSearchQuery(searchInput);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleSearchClick();
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setNewApplicantName('');
        setNewOwnerName(currentUser ? currentUser.id : '');
        setModalError(null);
        setModalLoading(false);
    };

    const handleCreateOpportunity = async (event) => {
        event.preventDefault();
        if (!newApplicantName || !newOwnerName) {
            setModalError('求職者名、担当者は必須です。');
            return;
        }
        setModalLoading(true);
        setModalError(null);
        try {
            const response = await api.fetch('/opportunities/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    applicant_name: newApplicantName,
                    owner_id: newOwnerName,
                }),
            });
            if (!response) return;
            if (!response.ok) {
                const errorMessage = await handleApiError(response, '作成に失敗しました。');
                throw new Error(errorMessage);
            }
            const newOpp = await response.json();
            setModalLoading(false);
            setIsModalOpen(false);
            navigate(`/opportunity/${newOpp.id}`);
        } catch (err) {
            setModalLoading(false);
            setModalError(err.message);
        }
    };

    // --- フィードバック振り返り ---
    const handleGenerateFeedbackSummary = async () => {
        if (summaryLoading) return;
        setSummaryLoading(true);
        setSummaryError(null);
        try {
            const response = await api.fetch('/users/me/generate_feedback_summary', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response) return;
            if (!response.ok) {
                const errorMessage = await handleApiError(response, '振り返りの生成に失敗しました。');
                throw new Error(errorMessage);
            }
            const data = await response.json();
            setSummaryResult(data.feedback_summary);
        } catch (err) {
            console.error(err);
            setSummaryError(err.message);
        } finally {
            setSummaryLoading(false);
        }
    };

    if (loading) return <SkeletonLoader variant="list" rows={3} />;

    return (
        <div className="dashboard-container">
            <AiResultModal
                loading={summaryLoading}
                loadingMessage="振り返りを生成中..."
                result={summaryResult}
                title="✨ フィードバック振り返り"
                error={summaryError}
                onClose={() => setSummaryResult(null)}
                onErrorClose={() => setSummaryError(null)}
            />

            <h1 className="page-title">マイページ</h1>
            {currentUser && (
                <div className="dashboard-greeting-row">
                    <p className="dashboard-greeting">
                        {currentUser.user_name} さん、こんにちは
                    </p>
                    {currentUser.has_ai_access && (
                        <button
                            className="dashboard-btn-ai-summary"
                            onClick={handleGenerateFeedbackSummary}
                            disabled={summaryLoading}
                        >
                            ✨ AI振り返り
                        </button>
                    )}
                </div>
            )}

            {/* === セクション1: 進行中の案件 === */}
            <div className="dashboard-card dashboard-card-full">
                <div className="dashboard-card-header">
                    <h2>進行中の案件</h2>
                    <span className="dashboard-count-badge">{activeOpps.total}</span>
                </div>

                <div className="dashboard-toolbar">
                    <div className="dashboard-search-row">
                        <input
                            type="text"
                            placeholder="求職者名で検索..."
                            className="search-input"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                        />
                        <button className="dashboard-btn-search" onClick={handleSearchClick}>
                            検索
                        </button>
                    </div>
                    <button className="dashboard-btn-create" onClick={handleOpenModal}>
                        + 新規案件
                    </button>
                </div>

                {activeOpps.items.length > 0 ? (
                    <>
                        <div className="table-responsive">
                            <table>
                                <thead>
                                    <tr>
                                        <th>求職者名</th>
                                        <th>担当者</th>
                                        <th>状況</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeOpps.items.map(opp => (
                                        <tr
                                            key={opp.id}
                                            className="clickable-row"
                                            onClick={() => navigate(`/opportunity/${opp.id}`)}
                                        >
                                            <td>
                                                <TruncatedCell text={opp.applicant_name || '-'} />
                                            </td>
                                            <td>{opp.owner_name}</td>
                                            <td>
                                                {(() => {
                                                    const label = opp.stage_name || '進行中';
                                                    return (
                                                        <div className="status-badge compact ongoing" title={label}>
                                                            <span className="status-badge-dot"></span>
                                                            <span className="status-badge-label">{label}</span>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {activeOpps.totalPages > 1 && (
                            <Pagination
                                currentPage={activeOpps.page}
                                totalPages={activeOpps.totalPages}
                                totalItems={activeOpps.total}
                                perPage={activeOpps.perPage}
                                onPageChange={activeOpps.setPage}
                            />
                        )}
                    </>
                ) : (
                    <p className="no-data">
                        {searchQuery ? '検索条件に一致する案件がありません。' : '進行中の案件はありません。'}
                    </p>
                )}
            </div>

            <div className="dashboard-grid">
                {/* === セクション2: 直近14日の活動 === */}
                <div className="dashboard-card">
                    <div className="dashboard-card-header">
                        <h2>直近{DAYS_RANGE}日の活動</h2>
                    </div>
                    {meetings.items.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>活動名</th>
                                            <th>求職者名</th>
                                            <th>活動日</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {meetings.items.map(meeting => (
                                            <tr
                                                key={meeting.id}
                                                className="clickable-row"
                                                onClick={() => navigate(`/activity/${meeting.id}`)}
                                            >
                                                <td>
                                                    <TruncatedCell text={meeting.name} maxLength={20} />
                                                </td>
                                                <td>{meeting.opportunity_applicant_name || '-'}</td>
                                                <td>{meeting.meeting_date}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meetings.totalPages > 1 && (
                                <Pagination
                                    currentPage={meetings.page}
                                    totalPages={meetings.totalPages}
                                    totalItems={meetings.total}
                                    perPage={meetings.perPage}
                                    onPageChange={meetings.setPage}
                                />
                            )}
                        </>
                    ) : (
                        <p className="no-data">直近{DAYS_RANGE}日の活動はありません。</p>
                    )}
                </div>

                {/* === セクション3: 直近14日の終了案件 === */}
                <div className="dashboard-card">
                    <div className="dashboard-card-header">
                        <h2>直近{DAYS_RANGE}日の終了案件</h2>
                    </div>
                    {closedOpps.items.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>求職者名</th>
                                            <th>終了日</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {closedOpps.items.map(opp => (
                                            <tr
                                                key={opp.id}
                                                className="clickable-row"
                                                onClick={() => navigate(`/opportunity/${opp.id}`)}
                                            >
                                                <td>
                                                    <TruncatedCell text={opp.applicant_name || '-'} />
                                                </td>
                                                <td>{opp.close_date || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {closedOpps.totalPages > 1 && (
                                <Pagination
                                    currentPage={closedOpps.page}
                                    totalPages={closedOpps.totalPages}
                                    totalItems={closedOpps.total}
                                    perPage={closedOpps.perPage}
                                    onPageChange={closedOpps.setPage}
                                />
                            )}
                        </>
                    ) : (
                        <p className="no-data">直近{DAYS_RANGE}日に終了した案件はありません。</p>
                    )}
                </div>
            </div>

            {/* === パフォーマンスダッシュボードへの入口 (SCR-E-002 ITEM-E2-002、Issue #1958) ===
                別の画面へ移るだけのため、ボタンでなく文字のリンクにする */}
            <p className="dashboard-performance-link-row">
                <Link to="/dashboard" className="dashboard-performance-link">📊 パフォーマンスダッシュボード</Link>
            </p>

            {/* === 案件作成モーダル === */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <form onSubmit={handleCreateOpportunity}>
                            <h3 className="modal-title">新規案件作成</h3>
                            <div className="form-group">
                                <label htmlFor="applicantName">求職者名 *</label>
                                <input
                                    id="applicantName"
                                    type="text"
                                    value={newApplicantName}
                                    onChange={(e) => setNewApplicantName(e.target.value)}
                                    disabled={modalLoading}
                                    placeholder="例: 田中 太郎"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="ownerSelect">担当者 *</label>
                                <UserSelect
                                    id="ownerSelect"
                                    value={newOwnerName}
                                    onChange={(val) => setNewOwnerName(val)}
                                    placeholder="担当者を選択..."
                                    disabled={modalLoading}
                                />
                            </div>
                            {modalError && (
                                <p className="modal-error-message">{modalError}</p>
                            )}
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="modal-button-cancel"
                                    onClick={() => setIsModalOpen(false)}
                                    disabled={modalLoading}
                                >
                                    キャンセル
                                </button>
                                <button
                                    type="submit"
                                    className="modal-button-confirm"
                                    disabled={modalLoading}
                                >
                                    {modalLoading ? '作成中...' : '作成'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DashboardPage;
